<template>
  <div class="mobile-bottom-nav" v-if="isMobile">
    <nav class="bottom-nav-container">
      <router-link 
        v-for="item in navItems" 
        :key="item.path"
        :to="item.path"
        class="nav-item"
        :class="{ 'active': isActive(item.path) }"
      >
        <i :class="item.icon"></i>
        <span class="nav-label">{{ item.label }}</span>
        <span v-if="item.badge && item.badge > 0" class="badge">{{ item.badge }}</span>
      </router-link>
    </nav>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useRoute } from 'vue-router'
import { useCartStore } from '@/stores/cart'
import { useUserStore } from '@/stores/user'
import { API_BASE_URL } from '@/utils/config'
import { authFetch } from '@/utils/authFetch'

const route = useRoute()
const cartStore = useCartStore()
const userStore = useUserStore()

const isMobile = ref(false)
const menuPages = ref<any[]>([])

// Check if mobile
const checkMobile = () => {
  isMobile.value = window.innerWidth <= 768
}

// Load navigation settings from API
const loadNavigationSettings = async () => {
  try {
    const [navigationResponse, pagesResponse] = await Promise.all([
      fetch(`${API_BASE_URL}/settings.php?action=get-navigation-settings`),
      fetch(`${API_BASE_URL}/pages.php?action=list&status=published&menu_only=true`)
    ])
    
    const navigationData = await navigationResponse.json()
    const pagesData = await pagesResponse.json()
    
    let allMenuPages: any[] = []
    
    if (navigationData.success && navigationData.data && navigationData.data.pages) {
      const configuredPages = navigationData.data.pages
        .filter((page: any) => page.visible)
        .sort((a: any, b: any) => {
          const aOrder = typeof a.order === 'number' ? a.order : parseInt(a.order) || 999
          const bOrder = typeof b.order === 'number' ? b.order : parseInt(b.order) || 999
          return aOrder - bOrder
        })
      
      allMenuPages = [...configuredPages]
    }
    
    // Add dynamic pages from database
    if (pagesData.success && pagesData.data) {
      const dynamicPages = pagesData.data
        .filter((page: any) => page.show_in_menu)
        .map((page: any) => {
          const pageKey = `page-${page.id}`
          const existingPage = allMenuPages.find((p: any) => p.id === pageKey)
          
          if (existingPage) return null
          
          return {
            id: pageKey,
            title: page.menu_title || page.title,
            path: `/${page.slug}`,
            icon: page.menu_icon || 'bi bi-file-text',
            visible: true,
            order: page.menu_order || 999,
            type: 'dynamic'
          }
        })
        .filter(Boolean)
      
      allMenuPages = [...allMenuPages, ...dynamicPages]
    }
    
    // Sort all pages by order
    allMenuPages.sort((a: any, b: any) => {
      const aOrder = typeof a.order === 'number' ? a.order : parseInt(a.order) || 999
      const bOrder = typeof b.order === 'number' ? b.order : parseInt(b.order) || 999
      return aOrder - bOrder
    })
    
    menuPages.value = allMenuPages
  } catch (error) {
    console.error('Failed to load navigation settings:', error)
  }
}

onMounted(() => {
  checkMobile()
  window.addEventListener('resize', checkMobile)
  loadNavigationSettings()
})

onUnmounted(() => {
  window.removeEventListener('resize', checkMobile)
})

// Navigation items - use first 4-5 items from menuPages or fallback
const navItems = computed(() => {
  const items: any[] = []
  
  // If we have menu pages from settings, use them (limit to 4)
  if (menuPages.value.length > 0) {
    const visiblePages = menuPages.value.slice(0, 4)
    visiblePages.forEach(page => {
      items.push({
        path: page.path,
        icon: page.icon,
        label: page.title,
        badge: page.path === '/cart' ? cartStore.itemCount : 0
      })
    })
  } else {
    // Fallback to default items
    items.push(
      {
        path: '/',
        icon: 'bi bi-house-fill',
        label: 'Αρχική'
      },
      {
        path: '/tours',
        icon: 'bi bi-map-fill',
        label: 'Εκδρομές'
      },
      {
        path: '/shop',
        icon: 'bi bi-shop',
        label: 'Κατάστημα'
      },
      {
        path: '/cart',
        icon: 'bi bi-cart-fill',
        label: 'Καλάθι',
        badge: cartStore.itemCount
      }
    )
  }

  // Always add user/login as 5th item
  if (userStore.isLoggedIn) {
    items.push({
      path: '/profile',
      icon: 'bi bi-person-fill',
      label: 'Προφίλ',
      badge: 0
    })
  } else {
    items.push({
      path: '/login',
      icon: 'bi bi-box-arrow-in-right',
      label: 'Σύνδεση',
      badge: 0
    })
  }

  return items
})

const isActive = (path: string) => {
  if (path === '/') {
    return route.path === '/'
  }
  return route.path.startsWith(path)
}
</script>

<style scoped>
.mobile-bottom-nav {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  background: #ffffff;
  border-top: 1px solid #e0e0e0;
  box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.1);
  padding-bottom: env(safe-area-inset-bottom); /* iOS safe area */
}

.bottom-nav-container {
  display: flex;
  justify-content: space-around;
  align-items: center;
  height: 60px;
  max-width: 100%;
}

.nav-item {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  height: 100%;
  color: #666;
  text-decoration: none;
  transition: all 0.2s ease;
  -webkit-tap-highlight-color: transparent;
  min-width: 44px; /* Minimum touch target */
}

.nav-item i {
  font-size: 22px;
  margin-bottom: 2px;
  transition: transform 0.2s ease;
}

.nav-label {
  font-size: 11px;
  font-weight: 500;
  margin-top: 2px;
}

.nav-item:active {
  background-color: rgba(0, 0, 0, 0.05);
}

.nav-item.active {
  color: #007bff;
}

.nav-item.active i {
  transform: scale(1.1);
}

.badge {
  position: absolute;
  top: 8px;
  right: 50%;
  transform: translateX(10px);
  background: #dc3545;
  color: white;
  border-radius: 10px;
  padding: 2px 6px;
  font-size: 10px;
  font-weight: bold;
  min-width: 18px;
  text-align: center;
}

/* Dark mode support */
@media (prefers-color-scheme: dark) {
  .mobile-bottom-nav {
    background: #1a1a1a;
    border-top-color: #333;
  }

  .nav-item {
    color: #aaa;
  }

  .nav-item.active {
    color: #4a9eff;
  }

  .nav-item:active {
    background-color: rgba(255, 255, 255, 0.05);
  }
}

/* Add spacing to body when bottom nav is visible */
@media (max-width: 768px) {
  :global(body) {
    padding-bottom: calc(60px + env(safe-area-inset-bottom));
  }
}
</style>
